import type { ReactNode } from 'react';
import { css, styled } from 'styled-components';

import { Box } from 'components/Box/Box';
import { LegacyButton } from 'components/LegacyButton/LegacyButton';
import { Text } from 'components/Text/Text';
import {
  colors,
  cssBreakpoints,
  fontSizesByType,
  mediaRetina,
  spacing,
  transition,
} from 'theme/theme';

export const DeleteButton = styled(LegacyButton)<{ $large?: boolean }>`
  transition: all ${transition};
  color: ${colors.lightContentGrey};

  &:hover,
  &:focus {
    color: ${colors.brandBlue};
  }
  ${(props) =>
    props.$large &&
    `
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  box-shadow: 0 0 0 2px transparent inset;
  &:hover {
    color: ${colors.alertRed};
    box-shadow: 0 0 0 2px ${colors.alertRed} inset;
  }
  `}
`;

const HoverOverlayBase = styled(Box)<{
  $mouseActive?: boolean;
  $loading?: boolean;
}>`
  position: absolute;
`;

export const HoverOverlaySmall = styled(HoverOverlayBase)`
  inset: 0;
  transition: opacity ${transition};
  border-radius: 4px;
  background: ${colors.softBlue};
  opacity: ${(props) => (props.$mouseActive && !props.$loading ? 1 : 0)};
`;

export const HoverOverlayLarge = styled(HoverOverlayBase)<{
  $mouseActive?: boolean;
  $loading?: boolean;
}>`
  inset: 0;
  transition:
    opacity ${transition},
    outline ${transition},
    background ${transition};
  border-radius: 8px;
  outline: ${(props) =>
    props.$mouseActive || props.$loading
      ? `4px solid ${colors.softBlue}`
      : `0 solid ${colors.inputGrey}`};
  background: ${(props) =>
    props.$mouseActive || props.$loading ? colors.softBlue : colors.inputGrey};
`;

export const DropBox = styled(Box)<{ $hasFile?: boolean; $large?: boolean }>`
  color: ${(props) =>
    props.$hasFile || props.$large
      ? colors.lightContentGrey
      : colors.brandBlue};
  transition: all ${transition};

  & > * {
    position: relative;
    ${(props) =>
      props.$large
        ? ''
        : `
    border: 1px solid ${props.$hasFile ? colors.elementGrey : colors.brandBlue};
    ${mediaRetina} {
      border-width: 1.5px;
    }
    `}
    border-radius: 8px;
    transition: all ${transition};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 6px;
    opacity: 0;
    transition: all ${transition};
  }
`;

export const DropContainer = styled(Box)<{
  $mouseActive?: boolean;
  $large?: boolean;
  $dragActive?: boolean;
}>`
  padding-left: 10px;
  padding-right: 10px;

  &,
  * {
    pointer-events: none;
  }
  ${DeleteButton}, a {
    pointer-events: auto;
  }
  ${(props) =>
    props.$mouseActive
      ? css`
          ${DropBox} {
            & > * {
              border-color: ${colors.brandBlue};
              ${props.$large
                ? `
          color: ${colors.brandBlue};
          `
                : `
          background: ${colors.brandBlue};
          color: white;
          `}
            }

            &::before {
              opacity: 1;
            }
          }
        `
      : undefined}
  ${(props) =>
    props.$dragActive
      ? `
    * {
      pointer-events: none !important;
    }
  `
      : undefined}
`;

export const FileLink = styled.a<{ $large?: boolean }>`
  ${(props) =>
    props.$large &&
    `
  color: ${colors.mediumContentGrey};
  `}
`;

export const FilenameText = styled.div<{ $large?: boolean }>`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  vertical-align: middle;
  ${(props) =>
    props.$large
      ? `
    max-width: 300px;
    @media all and (min-width: ${cssBreakpoints.xsUp}) {
      max-width: none;
      overflow: visible;
      white-space: normal;
      text-overflow: clip;
    }
  `
      : `
    max-width: 200px;
  `}
`;

export const ContentLargeWrapper = styled.div`
  position: relative;
  display: flex;
  height: 192px;
  width: 100%;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
`;

export function ContentLargeHeader({ children }: { children: ReactNode }) {
  return (
    <Text
      tag="span"
      color={colors.mediumContentGrey}
      fontSize={fontSizesByType.h3}
    >
      {children}
    </Text>
  );
}

export const ContentLargeCTA = styled.span`
  display: flex;
  flex-direction: column;
  gap: ${spacing[16]};
  align-items: center;
  justify-content: center;
`;
